<script lang="ts" setup>
import { ref, provide, watch } from 'vue'
import { defineProps } from 'vue'
//echarts
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'

import {
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components'

import VChart, { THEME_KEY } from 'vue-echarts'

interface SensorReading {
  timestamp: string
  temperature: number
  oxygen: number
  humidity?: number
  batteryLevel?: number
}

const props = defineProps({
  sensorData: {
    type: Array as () => SensorReading[],
    required: true,
  },
})

// echarts
use([
  LineChart,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
])

provide(THEME_KEY, 'light')

const option = ref({
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    formatter: (params: Array<{axisValue: string, color: string, seriesName: string, value: number}>) => {
      const timestamp = params[0].axisValue
      let tooltipContent = `<div style="padding: 8px;"><strong>${timestamp}</strong><br/>`
      
      params.forEach((param) => {
        const color = param.color
        const seriesName = param.seriesName
        const value = param.value
        const unit = seriesName === 'Temperatura' ? '°C' : '%'
        tooltipContent += `<span style="color: ${color};">●</span> ${seriesName}: ${value}${unit}<br/>`
      })
      
      tooltipContent += '</div>'
      return tooltipContent
    }
  },
  legend: {
    data: ['Temperatura', 'Oxígeno'],
    top: 10,
    textStyle: {
      fontSize: 12
    }
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '15%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [] as string[],
    axisLabel: {
      rotate: 45,
      fontSize: 10,
      formatter: (value: string) => {
        const date = new Date(value)
        return date.toLocaleDateString('es-ES', { 
          month: 'short', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }
  },
  yAxis: [
    {
      type: 'value',
      name: 'Temperatura (°C)',
      position: 'left',
      axisLabel: {
        formatter: '{value}°C',
        color: '#FF6B35'
      },
      axisLine: {
        lineStyle: {
          color: '#FF6B35'
        }
      },
      min: function(value: {min: number, max: number}) {
        return Math.floor(value.min - 2)
      },
      max: function(value: {min: number, max: number}) {
        return Math.ceil(value.max + 2)
      }
    },
    {
      type: 'value',
      name: 'Oxígeno (%)',
      position: 'right',
      axisLabel: {
        formatter: '{value}%',
        color: '#4CAF50'
      },
      axisLine: {
        lineStyle: {
          color: '#4CAF50'
        }
      },
      min: 0,
      max: 25
    }
  ],
  dataZoom: [
    {
      type: 'slider',
      show: true,
      xAxisIndex: [0],
      start: 0,
      end: 100,
      height: 20,
      bottom: 5
    }
  ],
  series: [
    {
      name: 'Temperatura',
      type: 'line',
      yAxisIndex: 0,
      data: [] as number[],
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        color: '#FF6B35',
        width: 3
      },
      itemStyle: {
        color: '#FF6B35'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(255, 107, 53, 0.3)' },
            { offset: 1, color: 'rgba(255, 107, 53, 0.1)' }
          ]
        }
      }
    },
    {
      name: 'Oxígeno',
      type: 'line',
      yAxisIndex: 1,
      data: [] as number[],
      smooth: true,
      symbol: 'diamond',
      symbolSize: 6,
      lineStyle: {
        color: '#4CAF50',
        width: 3
      },
      itemStyle: {
        color: '#4CAF50'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(76, 175, 80, 0.3)' },
            { offset: 1, color: 'rgba(76, 175, 80, 0.1)' }
          ]
        }
      }
    }
  ]
})

const chartRef = ref<InstanceType<typeof VChart> | null>(null)

const updateChartData = (data: SensorReading[]) => {
  const timestamps = data.map(item => item.timestamp)
  const temperatureData = data.map(item => item.temperature)
  const oxygenData = data.map(item => item.oxygen)

  option.value.xAxis.data = timestamps
  option.value.series[0].data = temperatureData
  option.value.series[1].data = oxygenData
}

watch(
  () => props.sensorData,
  (newData) => {
    updateChartData(newData)
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-chart ref="chartRef" class="chart" :option="option" style="height: 300px; width: 100%" />
</template>
